/**
 * Scene Editor Derived State
 * Feature: V2 3-column Reel Scene Editor
 *
 * Extracted from SceneEditorContainer to keep file under 300 lines.
 * Contains derived/computed values for the scene editor page.
 */

import { useMemo } from 'react';
import type { Id } from '../../convex/_generated/dataModel';
import type { IReelScene, IReelSceneEffective } from '@/@types/reelScenes';
import type { ISceneVoiceover } from '@/@types/voices';
import { getEffectiveSceneValues, calculateTotalDuration } from '@/@types/reelScenes';
import { countScenesWithoutImages } from '@/@types/sceneImageGeneration';

// ============================================================================
// Types
// ============================================================================

interface IReferencePerson {
  _id: Id<'referencePersons'>;
  name: string;
  isDefault?: boolean;
}

interface IClonedVoice {
  _id: Id<'clonedVoices'>;
  name: string;
}

interface IAvatarData {
  referencePersonName?: string;
  imageUrl: string;
}

interface ISceneEditorDerivedDeps {
  scenes: IReelScene[];
  selectedScene: IReelScene | null;
  defaultVoiceId: Id<'clonedVoices'> | null;
  activeVoices: IClonedVoice[];
  persons: IReferencePerson[];
  voiceoversMap: Map<string, ISceneVoiceover> | undefined;
  isRendering: boolean;
  avatarData: IAvatarData | null;
}

interface ISceneEditorDerivedReturn {
  scenesWithoutImages: number;
  totalDuration: number;
  hasVoices: boolean;
  hasScenes: boolean;
  effectiveValues: IReelSceneEffective | null;
  isGeneratingSelectedSceneImages: boolean;
  isGeneratingSelectedSceneVideo: boolean;
  canRenderSelectedScene: boolean;
  avatarName: string | null;
  voiceName: string | null;
  sceneThumbnailUrls: Map<string, string>;
  scriptPanelVoiceoversMap: Map<
    string,
    { audioUrl?: string; status: string; durationSeconds: number; voiceoverText: string }
  >;
}

// ============================================================================
// Hook
// ============================================================================

export function useSceneEditorDerivedState(
  deps: ISceneEditorDerivedDeps
): ISceneEditorDerivedReturn {
  const {
    scenes,
    selectedScene,
    defaultVoiceId,
    activeVoices,
    persons,
    voiceoversMap,
    isRendering,
    avatarData,
  } = deps;

  const scenesWithoutImages = useMemo(() => countScenesWithoutImages(scenes), [scenes]);

  const totalDuration = useMemo(() => calculateTotalDuration(scenes), [scenes]);

  const hasVoices = useMemo(() => activeVoices.length > 0, [activeVoices]);
  const hasScenes = scenes.length > 0;

  const effectiveValues = useMemo(
    () => (selectedScene ? getEffectiveSceneValues(selectedScene) : null),
    [selectedScene]
  );

  const isGeneratingSelectedSceneImages = selectedScene
    ? selectedScene.imageGenerationStatus === 'generating'
    : false;

  const isGeneratingSelectedSceneVideo = selectedScene
    ? selectedScene.videoGenerationStatus === 'generating' ||
      selectedScene.videoGenerationStatus === 'queued'
    : false;

  const effectiveSceneType = selectedScene
    ? (selectedScene.editedSceneType ?? selectedScene.sceneType)
    : null;
  const isAvatarOnly = effectiveSceneType === 'avatar_only';

  const canRenderSelectedScene = Boolean(
    selectedScene &&
    (isAvatarOnly
      ? selectedScene.libraryImageId
      : selectedScene.imageGenerationStatus === 'completed') &&
    defaultVoiceId &&
    !isRendering
  );

  const avatarName = useMemo(() => {
    if (!selectedScene) return null;
    // Check library avatar first (libraryImageId takes precedence)
    if (selectedScene.libraryImageId && avatarData) {
      return avatarData.referencePersonName ?? 'Uploaded Avatar';
    }
    // Fall back to reference person lookup
    const person = persons.find(p => p._id === selectedScene.referencePersonId);
    return person?.name ?? null;
  }, [selectedScene, persons, avatarData]);

  const voiceName = useMemo(() => {
    if (!defaultVoiceId) return null;
    const voice = activeVoices.find(v => v._id === defaultVoiceId);
    return voice?.name ?? null;
  }, [defaultVoiceId, activeVoices]);

  const sceneThumbnailUrls = useMemo(() => new Map<string, string>(), []);

  const scriptPanelVoiceoversMap = useMemo(() => {
    const map = new Map<
      string,
      { audioUrl?: string; status: string; durationSeconds: number; voiceoverText: string }
    >();
    if (voiceoversMap) {
      voiceoversMap.forEach((voiceover, sceneId) => {
        map.set(sceneId, {
          audioUrl: voiceover.audioUrl,
          status: voiceover.status,
          durationSeconds: voiceover.durationSeconds,
          voiceoverText: voiceover.voiceoverText,
        });
      });
    }
    return map;
  }, [voiceoversMap]);

  return {
    scenesWithoutImages,
    totalDuration,
    hasVoices,
    hasScenes,
    effectiveValues,
    isGeneratingSelectedSceneImages,
    isGeneratingSelectedSceneVideo,
    canRenderSelectedScene,
    avatarName,
    voiceName,
    sceneThumbnailUrls,
    scriptPanelVoiceoversMap,
  };
}
